/* eslint-disable react/no-array-index-key */
import { forwardRef, memo } from 'react'
import getTableCardPreview from '@/features/table-cards/helpers/getTableCardPreview'
import styles from './CardsPrintLayout.module.scss'
import { qrCodeCardModel } from '../../api/TableCardsManagementAPI.model'
import { ShapeType } from '@/shared/api/BaseAPI.model'
import { buildTableQrValue } from '../../helpers/buildTableQrValue'
import {
  usePrintGridStore,
  DEFAULT_PAGE_SIZE
} from '@/features/table-cards/store/usePrintGridStore'

const PX_PER_MM = 96 / 25.4

export type ExtendedQrCodeCardModel = qrCodeCardModel & {
  /** Backend table id used to build the QR value (rendered on the frontend). */
  tableId?: string
  tableNumber?: number
}

type ExtendedQrCodeCardModelWithPlaceholder = ExtendedQrCodeCardModel & {
  isPlaceholder?: boolean
}

interface CardsPrintLayoutProps {
  cards: ExtendedQrCodeCardModel[]
  logoImageUrl: string
}

interface PrintCardProps {
  card: ExtendedQrCodeCardModel
  logoImageUrl: string
  cardWidthMM: number
  cardHeightMM: number
}

function getCardSizeMM(card: {
  shape: ShapeType
  width?: number
  height?: number
  diameter?: number
}): { widthMM: number; heightMM: number } {
  if (card.shape === 'circle') {
    const d = card.diameter || 90
    return { widthMM: d, heightMM: d }
  }
  return {
    widthMM: card.width || 85,
    heightMM: card.height || 55
  }
}

function createPlaceholder(card: ExtendedQrCodeCardModel, index: number) {
  return {
    ...card,
    id: `placeholder-${index}`,
    isPlaceholder: true
  }
}

function paginateCards(cards: ExtendedQrCodeCardModel[], perPage: number) {
  const pages: ExtendedQrCodeCardModel[][] = []

  for (let i = 0; i < cards.length; i += perPage) {
    let slice = cards.slice(i, i + perPage)

    if (slice.length < perPage) {
      const diff = perPage - slice.length
      const sample = cards[0]

      const placeholders = Array.from({ length: diff }, (_, j) =>
        createPlaceholder(sample, i + slice.length + j)
      )

      slice = [...slice, ...placeholders]
    }

    pages.push(slice)
  }

  return pages
}

const CardsPrintLayout = forwardRef<HTMLDivElement, CardsPrintLayoutProps>(
  ({ cards, logoImageUrl }, ref) => {
    const { gap, tableCardsPerPage } = usePrintGridStore()

    const pageW = DEFAULT_PAGE_SIZE.widthMM
    const pageH = DEFAULT_PAGE_SIZE.heightMM

    if (cards.length === 0 || !tableCardsPerPage) return <div ref={ref} />

    const sample = cards[0]
    if (!sample) return <div ref={ref} />
    const { widthMM: cardW, heightMM: cardH } = getCardSizeMM(sample)

    const cols = Math.floor((pageW + gap.horizontal) / (cardW + gap.horizontal))
    const rows = Math.floor((pageH + gap.vertical) / (cardH + gap.vertical))
    const fittingPerPage = Math.min(cols * rows, tableCardsPerPage)
    const actualCols = Math.min(cols, tableCardsPerPage)
    const actualRows = Math.ceil(fittingPerPage / actualCols)

    const pages = paginateCards(cards, fittingPerPage)

    return (
      <div ref={ref} className={styles.printLayout}>
        <style type="text/css" media="print">{`
          @page { size: ${pageW}mm ${pageH}mm; margin: 0; }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        `}</style>
        {pages.map((page, idx) => (
          <div
            key={idx}
            className={styles.printPage}
            style={{
              width: `${pageW}mm`,
              height: `${pageH}mm`,
              display: 'grid',
              gridTemplateColumns: `repeat(${actualCols}, ${cardW}mm)`,
              gridTemplateRows: `repeat(${actualRows}, ${cardH}mm)`,
              columnGap: `${gap.horizontal}mm`,
              rowGap: `${gap.vertical}mm`,
              justifyContent: 'center',
              alignContent: 'center',
              placeItems: 'center'
            }}
          >
            {page.map((card, index) => (
              <PrintCard
                key={`${card.id}-${index}`}
                card={card}
                logoImageUrl={logoImageUrl}
                cardWidthMM={cardW}
                cardHeightMM={cardH}
              />
            ))}
          </div>
        ))}
      </div>
    )
  }
)

const PrintCard = memo(function PrintCard({
  card,
  logoImageUrl,
  cardWidthMM,
  cardHeightMM
}: PrintCardProps) {
  const { isPlaceholder } = card as ExtendedQrCodeCardModelWithPlaceholder

  if (isPlaceholder) {
    return <div className={styles.placeholder} />
  }

  const widthPx = Math.round(cardWidthMM * PX_PER_MM)
  const heightPx = Math.round(cardHeightMM * PX_PER_MM)

  return (
    <div className={styles.printCardWrapper} style={{ overflow: 'hidden' }}>
      {getTableCardPreview({
        logoImageUrl,
        shape: card.shape,
        withNfc: card.withNfc,
        languageCode: card.languageCode,
        align: card.alignment,
        backgroundColorValue: card.backgroundColor,
        contentColor: card.contentColor,
        contrastColor: card.contrastColor,
        backgroundImage: card.backgroundImage?.url,
        width: widthPx,
        height: heightPx,
        tableNumber: card.tableNumber,
        qrCodeValue: buildTableQrValue(card.tableId)
      })}
    </div>
  )
})

export default CardsPrintLayout
